'use client';

import { SESSION_STATUS } from '@/constants/auth';
import { PropsWithChildren, useEffect, useState, useTransition } from 'react';
import { useSession } from 'next-auth/react';
import { useSWRConfig } from 'swr';
import { type UserProfileDto } from '@/authApiClient';
import { PostLoginFlow } from './PostLoginFlow';
import { PostSignUpFlow } from './PostSignUpFlow';
import { usePrivate } from './PrivateContext';
import {
  useDatadogCorrelation,
  useInitializeContext,
  useRoleRedirect,
  useDatadogUser,
} from '@/hooks';
import { useLaunchDarklyUser } from '@/hooks/useLaunchDarklyUser';

interface PrivateProps extends PropsWithChildren {
  onRenderPublicSearchStateChange?: (shouldRenderPublicSearch: boolean) => void;
}

// Private mounts unconditionally from `PrivateGate` and always returns
// `children` — its tree position does not change when auth resolves, so the
// route subtree (and any state it holds) survives the LOADING →
// AUTHENTICATED transition. The auth-dependent side effects (PostLoginFlow,
// PostSignUpFlow, the context bootstrap) self-gate on `useSession()` and
// no-op while the session is loading or unauthenticated.

export function Private({
  children,
  onRenderPublicSearchStateChange = () => {},
}: PrivateProps) {
  const { data: session, status } = useSession();
  const isAuthenticated = status === SESSION_STATUS.AUTHENTICATED;
  const { mutate } = useSWRConfig();
  const [loadedUserProfile, setLoadedUserProfile] = useState<UserProfileDto>();

  const contextData = usePrivate();
  const {
    userOrganisations,
    setUserOrganisations,
    selectedOrganisationId,
    setSelectedOrganisationId,
    setShowSplashScreen,
    setContextData,
  } = contextData;

  const [isPending] = useTransition();

  useEffect(() => {
    if (isAuthenticated && session?.user && setContextData) {
      setContextData((prevData) => ({
        ...prevData,
        userId: session.user.internalUserId,
        userGuid: session.user.userGuid,
        userProfileGuid: session.user.userProfileGuid,
        email: session.user.email,
        isABRIStaff: session.user.isABRIStaff,
        entitlements: session.user.entitlements,
      }));
    }
  }, [isAuthenticated, session, setContextData]);

  useDatadogCorrelation();
  useInitializeContext(
    selectedOrganisationId,
    userOrganisations,
    setContextData,
    setUserOrganisations,
    setSelectedOrganisationId,
  );
  useDatadogUser(loadedUserProfile, contextData, selectedOrganisationId);
  useLaunchDarklyUser();

  const handleUserProfileLoaded = (userProfile: UserProfileDto) => {
    setLoadedUserProfile(userProfile);
    if (setContextData) {
      setContextData((prevContextData) => ({
        ...prevContextData,
        userProfile,
        reloadUserProfile: () => {
          mutate(`/api/UserProfile/${prevContextData?.userId}`);
        },
      }));
    }
  };

  const redirectByRole = useRoleRedirect(
    selectedOrganisationId,
    onRenderPublicSearchStateChange,
  );

  useEffect(() => {
    if (isAuthenticated && !isPending && setShowSplashScreen) {
      setShowSplashScreen(false);
    }
  }, [isAuthenticated, isPending, setShowSplashScreen]);

  // children come first in the fragment so their reconciliation index stays
  // at 0 regardless of whether the auth-gated siblings are mounted. Adding
  // siblings before children would shift every subsequent index by one and
  // force React to remount the route subtree when auth resolves.
  return (
    <>
      {children}
      {isAuthenticated && (
        <PostLoginFlow
          onUserProfileLoaded={handleUserProfileLoaded}
          onUserOrgAssociationsRetrieved={redirectByRole}
        />
      )}
      {isAuthenticated && <PostSignUpFlow onFinishFlow={redirectByRole} />}
    </>
  );
}
